import React from 'react';
import { motion } from 'framer-motion';
import {
    ShieldCheck, Lock, Eye, AlertTriangle, ShieldAlert,
    Terminal, ArrowRight, ChevronLeft, Wifi, Database,
    Search, ShieldOff, HardDrive, Key
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO_Component from '../../components/SEO';

const CyberSecurity = () => {
    const securityPillars = [
        { title: "Network Defense", desc: "Advanced firewalls and intrusion detection systems.", icon: Wifi },
        { title: "Encryption", desc: "Military-grade data protection for stored & sent data.", icon: Key },
        { title: "Penetration Testing", desc: "Simulated attacks to find and fix vulnerabilities.", icon: Terminal },
        { title: "Identity Mgmt", desc: "Zero-trust access and multi-factor authentication.", icon: Lock },
        { title: "Incident Response", desc: "Rapid remediation logic for digital threats.", icon: AlertTriangle },
        { title: "Compliance", desc: "Meeting global standards like GDPR and HIPAA.", icon: ShieldCheck }
    ];

    return (
        <div className="bg-[#02000c] min-h-screen text-white pt-20 overflow-hidden">
            <SEO_Component
                title="Cyber Security | Protect Your Digital Assets - MehareTech"
                description="Secure your business from evolving digital threats with MehareTech's cyber security audits, real-time threat detection, and military-grade encryption."
                keywords="cyber security audits, network defense, penetration testing, data encryption services, zero trust architecture, business threat remediation"
            />

            {/* Back to Services Link */}
            <div className="container mx-auto px-6 mb-4">
                <Link to="/services" className="inline-flex items-center gap-2 text-red-500 font-bold uppercase tracking-widest text-[10px] hover:text-white transition-colors group">
                    <ChevronLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
                    Back to Services
                </Link>
            </div>

            {/* Hero Section */}
            <section className="relative py-12 px-6">
                <div className="absolute top-0 right-0 w-1/3 h-full bg-red-600/10 blur-[120px] -z-10"></div>

                <div className="container mx-auto text-center max-w-4xl">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        <span className="text-red-500 font-black uppercase tracking-[0.3em] text-[10px] mb-2 block">
                            Digital Fortress
                        </span>
                        <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight tracking-tighter">
                            UNCOMPROMISED <span className="bg-gradient-to-r from-red-600 via-rose-500 to-orange-500 bg-clip-text text-transparent">SECURITY</span>
                        </h1>
                        <p className="text-lg text-slate-400 mb-8 max-w-2xl mx-auto font-medium">
                            We deploy advanced defensive layers to protect your brand from evolving digital threats.
                        </p>
                        <div className="flex justify-center">
                            <button className="px-8 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all flex items-center gap-2 text-xs">
                                Run Security Audit <ArrowRight size={18} />
                            </button>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Pillars Grid */}
            <section className="py-12 px-6 bg-slate-900/40 border-y border-white/5">
                <div className="container mx-auto">
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                        {securityPillars.map((pillar, i) => (
                            <motion.div
                                key={i}
                                className="p-6 bg-slate-900 border border-white/5 rounded-2xl hover:border-red-500/30 transition-all group"
                            >
                                <pillar.icon size={26} className="text-red-500 mb-4 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-bold mb-1">{pillar.title}</h3>
                                <p className="text-slate-400 text-[11px] leading-relaxed">{pillar.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Alert Section */}
            <section className="py-12 px-6">
                <div className="container mx-auto">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <div className="md:w-1/2">
                            <h2 className="text-3xl font-black mb-6 tracking-tight">
                                Proactive <span className="text-red-500">Protection</span>
                            </h2>
                            <div className="space-y-4">
                                <div className="p-4 bg-red-600/5 border border-red-600/20 rounded-xl flex items-center gap-4">
                                    <ShieldAlert className="text-red-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Threat Hunting</h4>
                                        <p className="text-[10px] text-slate-400">Discovering hidden threats before they can strike.</p>
                                    </div>
                                </div>
                                <div className="p-4 bg-white/5 border border-white/5 rounded-xl flex items-center gap-4">
                                    <Search className="text-red-500" size={24} />
                                    <div>
                                        <h4 className="font-bold text-sm">Full Vulnerability Scan</h4>
                                        <p className="text-[10px] text-slate-400">Deep analysis of your entire digital footprint.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="md:w-1/2">
                            <img
                                src="https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg"
                                alt="Security"
                                className="rounded-3xl shadow-xl w-full h-48 object-cover border border-white/10"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CyberSecurity;
